import _ from 'lodash';

import {
  finleyAssertUnreachable,
  error as commonError,
  finleyAssert,
} from '@finley-technologies/common';
import { FinleyAi as FinleyAiTypes } from '@finley-technologies/finley-types';

import { FinleyAiViewContext } from './types';

export const finleyAiSelectOptions: {
  value: FinleyAiTypes.FinleyAiType;
  label: string;
}[] = [{ value: 'athenaSqlGeneration' as const, label: 'Sql Generation' }].map(
  ({ value, label }) => ({
    value,
    label,
  }),
);

// TS does not like iterating on promptResponses because it is a Union Array,
// it needs to be narrowed first
export const returnIsThreadFailed = (
  thread: FinleyAiTypes.FinleyAiThreadWithPromptResponses,
): boolean => {
  switch (thread.type) {
    case 'athenaSqlGeneration':
      return (
        _.some(thread.promptResponses, (pr) => pr.status === 'failure') ||
        !thread.promptResponses.length
      );
    case 'singleCreditAgreementQuestion':
      return (
        _.some(thread.promptResponses, (pr) => pr.status === 'failure') ||
        !thread.promptResponses.length
      );
    default:
      return finleyAssertUnreachable(thread);
  }
};

export const returnIsTerminalStatus = (
  threadWithPromptResponses: FinleyAiTypes.FinleyAiThreadWithPromptResponses,
) => {
  if (returnIsThreadFailed(threadWithPromptResponses)) {
    return true;
  }

  const lastPromptResponse =
    threadWithPromptResponses.promptResponses[
      threadWithPromptResponses.promptResponses.length - 1
    ];
  finleyAssert(
    lastPromptResponse,
    'Last Finley Ai Prompt Response must exist if thread is in failed state.',
  );

  return FinleyAiTypes.FinleyAiPromptResponseTerminalStatuses.includes(
    lastPromptResponse.status,
  );
};

export const getPromptPlaceholder = ({
  type,
  loading,
  isThreadFailed,
}: {
  type: FinleyAiTypes.FinleyAiType;
  loading: boolean;
  isThreadFailed: boolean;
}): string => {
  if (loading) {
    return 'Thinking...';
  }

  if (isThreadFailed) {
    return 'This thread is in a failed state. Please start a new thread.';
  }
  switch (type) {
    case 'athenaSqlGeneration':
      return 'Ask a question';
    case 'singleCreditAgreementQuestion':
      throw new commonError.FinleyError(
        'singleCreditAgreementQuestion not implemented',
      );
    default:
      return finleyAssertUnreachable(type);
  }
};

export const convertToFinleyAiRequest = ({
  viewContext,
  prompt,
}: {
  viewContext: FinleyAiViewContext;
  prompt: string;
}): FinleyAiTypes.FinleyAiRequest => {
  switch (viewContext.type) {
    case 'athenaSqlGeneration':
      return {
        type: 'athenaSqlGeneration',
        subType: 'sqlGeneration',
        targetOrganizationId: viewContext.targetOrganizationId,
        tableNames: viewContext.tableNames,
        prompt,
        finleyAiThreadId: viewContext.finleyAiThreadId,
      };
    case 'singleCreditAgreementQuestion':
      return {
        type: 'singleCreditAgreementQuestion',
        subType: 'simpleQuestion',
        prompt,
        targetOrganizationId: viewContext.targetOrganizationId,
        finleyAiThreadId: viewContext.finleyAiThreadId,
        creditAgreementId: viewContext.creditAgreementId,
        creditFacilityId: viewContext.creditFacilityId,
      };
    default:
      return finleyAssertUnreachable(viewContext);
  }
};

export const initFinleyAiViewContext = ({
  type,
  targetOrganizationId,
  finleyAiThreadId,
}: {
  type: FinleyAiTypes.FinleyAiType;
  targetOrganizationId: string;
  finleyAiThreadId: string | null;
}): FinleyAiViewContext => {
  switch (type) {
    case 'athenaSqlGeneration':
      return {
        targetOrganizationId,
        type,

        finleyAiThreadId,
        tableNames: [],
      };
    case 'singleCreditAgreementQuestion':
      return {
        type,
        targetOrganizationId,
        finleyAiThreadId,
        creditAgreementId: null,
        creditFacilityId: 'placeholder',
      };
    default:
      return finleyAssertUnreachable(type);
  }
};

export const generateFinleyAiViewContextFromThread = ({
  thread,
}: {
  thread: FinleyAiTypes.FinleyAiThreadWithPromptResponses;
}): FinleyAiViewContext => {
  const { id: finleyAiThreadId } = thread;

  switch (thread.type) {
    case 'athenaSqlGeneration': {
      // we just need to bootstrap the tablenames for this thread
      // any sqlGeneration subtype will do, as tablenames are static once
      // a thread is created
      const sqlGenerationPromptResponse = _.find(
        thread.promptResponses,
        (pr) => pr.contextMetadata.subType === 'sqlGeneration',
      );

      if (sqlGenerationPromptResponse) {
        finleyAssert(
          sqlGenerationPromptResponse.contextMetadata.subType ===
            'sqlGeneration',
          `Context metadata subtype must be sqlGeneration, but got [subType:${sqlGenerationPromptResponse.contextMetadata.subType}]`,
        );
        return {
          targetOrganizationId: thread.targetOrganizationId,
          type: thread.type,
          finleyAiThreadId,
          tableNames: sqlGenerationPromptResponse.contextMetadata.tables.map(
            (table) => table.tableName,
          ),
        };
      }
      // this condition is a failed state, but gracefully handle elsewhere
      // instead of asserting here
      return {
        targetOrganizationId: thread.targetOrganizationId,
        type: thread.type,
        finleyAiThreadId,
        tableNames: [],
      };
    }
    case 'singleCreditAgreementQuestion': {
      // we just need to bootstrap the cfId / caId for this thread
      // any promptResponse will do, as these are static once
      // a thread is created
      const last = _.last(thread.promptResponses);

      return {
        targetOrganizationId: thread.targetOrganizationId,
        type: thread.type,
        finleyAiThreadId,
        creditAgreementId: last?.contextMetadata.creditAgreementId ?? null,
        creditFacilityId:
          last?.contextMetadata.creditAgreementId ?? 'placeholder',
      };
    }
    default:
      return finleyAssertUnreachable(thread);
  }
};

interface ParsedPromptResponse {
  value: string;
  type: 'sql' | 'plaintext';
}

// parses prompt result response into segments so that SQL can be rendered separately
export const parsePromptResponse = (input: string): ParsedPromptResponse[] => {
  const regex = /(<SQL>[\s\S]*?<\/SQL>)/;
  const parts = input.split(regex);
  const result: ParsedPromptResponse[] = [];

  parts.forEach((part) => {
    if (part.startsWith('<SQL>') && part.endsWith('</SQL>')) {
      result.push({
        value: part.slice(5, -6), // remove '<SQL>' and '</SQL>'
        type: 'sql',
      });
    } else {
      result.push({
        value: part.trimStart(), // remove leading linebreaks
        type: 'plaintext',
      });
    }
  });

  return result;
};

// monaco-editor does not natively support fit-content height
// so we will calculate the height the editor should roughly be
// based on the number of lines in the string it's displaying
export const getEditorHeight = (str: string) => {
  const numberOfLinesInString = (str.match(/\n/g) || []).length + 1;
  return `${numberOfLinesInString * 24}px`;
};
